import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CalendarOptions, DayCellContentArg } from '@fullcalendar/core';
import frLocale from '@fullcalendar/core/locales/fr';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DateFieldComponent } from '../../../shared/components/ui/date-field/date-field.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { VehiclesService } from '../../../shared/services/vehicles.service';
import { UnavailableRange, VehicleBlackout } from '../../../shared/types/availability.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { LocaleService } from '../../../shared/services/locale.service';
import { serverDate } from '../../../shared/helpers/dates';

/** Plage indisponible normalisée (timestamps) pour les comparaisons jour. */
interface BlockedRange {
  start: number;
  end: number;
  reason: string;
  rentalId: number | null;
  blackoutId: number | null;
}

/**
 * Calendrier de disponibilité d'un véhicule owner (§2.3b / #45) **+ gestion des
 * blocages manuels** (§2.3c / #63).
 *
 * Zone **disponible** (blanche) = intérieur de la fenêtre de mise à disposition
 * (`provisionStartDate` → `provisionEndDate`), hors réservations. Sont **grisées** :
 *  - les jours **avant** `provisionStartDate` et **après** `provisionEndDate` ;
 *  - les plages `unavailableRanges` (réservations `BOOKED` — vert, clic→location ;
 *    `NOT_PROVISIONED` — gris ; `OWNER_BLOCKED` — ambre, clic→suppression).
 *
 * L'owner peut **bloquer** une plage (« je garde mon véhicule ») via le bouton
 * « Bloquer un créneau » (modal date→date + motif), et **lever** un blocage
 * depuis la liste ou en cliquant le jour ambre. Journée entière = `T00:00:00→T23:59:59`.
 */
@Component({
  selector: 'app-vehicle-availability',
  standalone: true,
  imports: [
    FullCalendarModule,
    ReactiveFormsModule,
    DatePipe,
    ModalComponent,
    ConfirmModalComponent,
    DateFieldComponent, TPipe, LocalizedDatePipe],
  templateUrl: './vehicle-availability.component.html',
  styles: [`
    :host ::ng-deep .fc .fc-daygrid-day.nawam-off { background-color: #f3f4f6; }
    :host ::ng-deep .fc .fc-daygrid-day.nawam-off .fc-daygrid-day-number { color: #9ca3af; }
    :host ::ng-deep .fc .fc-daygrid-day.nawam-booked { background-color: rgba(11, 140, 0, 0.12); cursor: pointer; }
    :host ::ng-deep .fc .fc-daygrid-day.nawam-blocked { background-color: rgba(245, 158, 11, 0.18); cursor: pointer; }
    :host-context(.dark) ::ng-deep .fc .fc-daygrid-day.nawam-off { background-color: rgba(255, 255, 255, 0.04); }
    :host-context(.dark) ::ng-deep .fc .fc-daygrid-day.nawam-off .fc-daygrid-day-number { color: #6b7280; }
    :host-context(.dark) ::ng-deep .fc .fc-daygrid-day.nawam-booked { background-color: rgba(11, 140, 0, 0.22); }
    :host-context(.dark) ::ng-deep .fc .fc-daygrid-day.nawam-blocked { background-color: rgba(245, 158, 11, 0.26); }
  `],
})
export class VehicleAvailabilityComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) vehicleId!: number;
  /** Fenêtre de mise à disposition (ISO). `null` = pas de borne (toujours dispo). */
  @Input() provisionStart: string | null = null;
  @Input() provisionEnd: string | null = null;

  @ViewChild(FullCalendarComponent) private calendar?: FullCalendarComponent;

  private readonly locale = inject(LocaleService);

  private readonly vehiclesService = inject(VehiclesService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly gracePeriodHours = signal<number | null>(null);

  // ── Blocages manuels (#63) ────────────────────────────────────────────
  readonly blackouts = signal<VehicleBlackout[]>([]);
  readonly blackoutsLoading = signal(false);
  readonly modalOpen = signal(false);
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);
  readonly deleteTarget = signal<VehicleBlackout | null>(null);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  /** Borne min des inputs date (aujourd'hui) — le back refuse une période passée. */
  readonly todayStr = this.fmt(new Date());

  readonly blackoutForm = this.fb.group({
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    reason: [''],
  });

  /** Début de journée (ms) de la borne basse/haute de mise à dispo, ou null. */
  private provStartDay: number | null = null;
  private provEndDay: number | null = null;
  private blocked: BlockedRange[] = [];

  calendarOptions!: CalendarOptions;

  ngOnInit(): void {
    this.provStartDay = this.toDayStart(this.provisionStart);
    this.provEndDay = this.toDayStart(this.provisionEnd);

    this.calendarOptions = {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      // Ouvre sur le mois courant, sauf si la mise à dispo démarre plus tard.
      initialDate: this.provStartDay && this.provStartDay > Date.now() ? new Date(this.provStartDay) : new Date(),
      locale: frLocale,
      height: 'auto',
      firstDay: 1,
      headerToolbar: { left: 'prev,next today', center: 'title', right: '' },
      dayCellClassNames: (arg) => this.dayClasses(arg),
      dateClick: (arg) => this.onDateClick(arg),
    };
  }

  ngAfterViewInit(): void {
    this.fetch();
    this.loadBlackouts();
  }

  private fetch(): void {
    // Fenêtre interrogée = mise à dispo si connue, sinon [aujourd'hui, +12 mois].
    const today = new Date();
    const start = this.provisionStart ? new Date(this.provisionStart) : today;
    const end = this.provisionEnd
      ? new Date(this.provisionEnd)
      : new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

    this.loading.set(true);
    this.error.set(null);
    this.vehiclesService
      .availabilityCalendar(this.vehicleId, this.fmt(start), this.fmt(end))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.gracePeriodHours.set(res.gracePeriodHours ?? null);
          this.blocked = this.normalize(res.unavailableRanges ?? []);
          this.loading.set(false);
          // Re-render pour appliquer le grisage avec les plages fraîchement chargées.
          this.calendar?.getApi().render();
        },
        error: () => {
          this.blocked = [];
          this.loading.set(false);
          this.error.set('Calendrier indisponible pour le moment.');
          this.calendar?.getApi().render();
        },
      });
  }

  /** Liste des blocages actifs (pour la section supprimable sous le calendrier). */
  private loadBlackouts(): void {
    this.blackoutsLoading.set(true);
    this.vehiclesService
      .listBlackouts(this.vehicleId, 1, 100)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.blackouts.set(res.data ?? []);
          this.blackoutsLoading.set(false);
        },
        error: () => {
          this.blackouts.set([]);
          this.blackoutsLoading.set(false);
        },
      });
  }

  private normalize(ranges: UnavailableRange[]): BlockedRange[] {
    return ranges
      .map((r) => ({
        start: serverDate(r.start).getTime(),
        end: serverDate(r.end).getTime(),
        reason: r.reason,
        rentalId: r.rentalId ?? null,
        blackoutId: r.blackoutId ?? null,
      }))
      .filter((r) => Number.isFinite(r.start) && Number.isFinite(r.end));
  }

  /** Classes CSS d'une cellule-jour : grisée hors fenêtre / réservée / bloquée. */
  private dayClasses(arg: DayCellContentArg): string[] {
    const dayStart = this.startOfDay(arg.date).getTime();
    const dayEnd = dayStart + 86_400_000;

    if (this.provStartDay != null && dayStart < this.provStartDay) return ['nawam-off'];
    if (this.provEndDay != null && dayStart > this.provEndDay) return ['nawam-off'];

    const hit = this.blocked.find((r) => r.start < dayEnd && r.end > dayStart);
    if (!hit) return [];
    if (hit.reason === 'BOOKED') return ['nawam-off', 'nawam-booked'];
    if (hit.reason === 'OWNER_BLOCKED') return ['nawam-off', 'nawam-blocked'];
    return ['nawam-off'];
  }

  private onDateClick(arg: DateClickArg): void {
    const dayStart = this.startOfDay(arg.date).getTime();
    const dayEnd = dayStart + 86_400_000;
    const hit = this.blocked.find((r) => r.start < dayEnd && r.end > dayStart);
    if (!hit) return;
    // Réservation → ouvre la fiche location. Blocage owner → propose la levée.
    if (hit.reason === 'BOOKED' && hit.rentalId) {
      void this.router.navigate(['/my-rentals', hit.rentalId]);
    } else if (hit.reason === 'OWNER_BLOCKED' && hit.blackoutId != null) {
      const bo = this.blackouts().find((b) => b.id === hit.blackoutId);
      if (bo) this.askDelete(bo);
    }
  }

  // ── Ajout d'un blocage ────────────────────────────────────────────────
  openModal(): void {
    this.submitError.set(null);
    this.blackoutForm.reset({ startDate: '', endDate: '', reason: '' });
    this.modalOpen.set(true);
  }

  closeModal(): void {
    if (this.submitting()) return;
    this.modalOpen.set(false);
  }

  submitBlackout(): void {
    if (this.blackoutForm.invalid) {
      this.blackoutForm.markAllAsTouched();
      return;
    }
    const { startDate, endDate, reason } = this.blackoutForm.getRawValue();
    if (endDate < startDate) {
      this.submitError.set(this.locale.t('vehicles.endDateAfterStart'));
      return;
    }

    this.submitting.set(true);
    this.submitError.set(null);
    this.vehiclesService
      .createBlackout(this.vehicleId, {
        // Journée entière : bornes exactes attendues par le back.
        startDate: `${startDate}T00:00:00`,
        endDate: `${endDate}T23:59:59`,
        reason: reason.trim() || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.modalOpen.set(false);
          this.fetch();
          this.loadBlackouts();
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          this.submitError.set(
            toErrorMessage(err, "Le blocage n'a pas pu être créé.", {
              409: 'errors.slotHasBooking',
              400: 'errors.datesInvalid',
            }),
          );
        },
      });
  }

  // ── Suppression d'un blocage ──────────────────────────────────────────
  askDelete(bo: VehicleBlackout): void {
    this.deleteError.set(null);
    this.deleteTarget.set(bo);
  }

  cancelDelete(): void {
    if (this.deleting()) return;
    this.deleteTarget.set(null);
  }

  confirmDelete(): void {
    const bo = this.deleteTarget();
    if (!bo) return;
    this.deleting.set(true);
    this.deleteError.set(null);
    this.vehiclesService
      .deleteBlackout(this.vehicleId, bo.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.deleteTarget.set(null);
          this.fetch();
          this.loadBlackouts();
        },
        error: (err: unknown) => {
          this.deleting.set(false);
          this.deleteError.set(toErrorMessage(err, "La levée du blocage a échoué."));
        },
      });
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private toDayStart(iso: string | null): number | null {
    if (!iso) return null;
    const d = serverDate(iso);
    return Number.isFinite(d.getTime()) ? this.startOfDay(d).getTime() : null;
  }

  /** Date → `yyyy-MM-dd` (format attendu par le back). */
  private fmt(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
